import { useState, useEffect, useCallback } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import { Modal } from "@/components/ui/modal";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { handleApiError } from "@/services/api";
import { useIsOwner } from "@/hooks/useRole";
import {
  vaccinationService,
  validateScheduleEntry,
  routeLabel,
  VACCINATION_ROUTES,
  type VaccinationRoute,
  type VaccinationScheduleEntry,
} from "@/services/vaccinationService";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

/** Day 7 reads as "1 week", day 28 as "4 weeks" — how a programme is spoken. */
function ageLabel(days: number): string {
  if (days === 0) return "Day-old";
  if (days % 7 === 0) {
    const weeks = days / 7;
    return `${weeks} ${weeks === 1 ? "week" : "weeks"}`;
  }
  return `Day ${days}`;
}

export default function VaccinationSchedulePage() {
  const isOwner = useIsOwner();

  const [entries, setEntries] = useState<VaccinationScheduleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<VaccinationScheduleEntry | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [dayOfAge, setDayOfAge] = useState("");
  const [vaccine, setVaccine] = useState("");
  const [route, setRoute] = useState<VaccinationRoute>("eye_drop");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEntries(await vaccinationService.listSchedule());
      setListError("");
    } catch (err) {
      // Not silent: an empty table and a failed request look identical, and one
      // of them means "this farm has no programme", which is a false statement
      // to make on the back of a network error.
      setEntries([]);
      setListError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDayOfAge("");
    setVaccine("");
    setRoute("eye_drop");
    setNotes("");
    setError("");
    setModalOpen(true);
  };

  const openEdit = (entry: VaccinationScheduleEntry) => {
    setEditing(entry);
    setDayOfAge(String(entry.day_of_age));
    setVaccine(entry.vaccine);
    setRoute(entry.route);
    setNotes(entry.notes ?? "");
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      day_of_age: Number(dayOfAge),
      vaccine: vaccine.trim(),
      route,
      notes: notes.trim() === "" ? null : notes.trim(),
    };
    const problem = validateScheduleEntry(payload);
    if (problem) {
      setError(problem);
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (editing) {
        await vaccinationService.updateScheduleEntry(editing.id, payload);
      } else {
        await vaccinationService.createScheduleEntry(payload);
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry: VaccinationScheduleEntry) => {
    if (
      !window.confirm(
        `Remove ${entry.vaccine} at ${ageLabel(entry.day_of_age).toLowerCase()} from the programme? ` +
          `Doses already recorded against it are kept.`,
      )
    )
      return;
    try {
      await vaccinationService.deleteScheduleEntry(entry.id);
      await load();
    } catch (err) {
      setListError(handleApiError(err));
    }
  };

  return (
    <>
      <PageMeta
        title="Vaccination programme | Poultry Book"
        description="The vaccination schedule every flock is measured against."
      />
      <PageBreadcrumb pageTitle="Vaccination programme" />

      <div className="space-y-6">
        <div className="card p-5 lg:p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            One entry per dose, by the bird&rsquo;s age rather than a date, so
            the same programme applies to every batch. Each flock&rsquo;s own
            due dates are worked out from the day it arrived — see the
            Vaccination section on a flock&rsquo;s page.
          </p>
        </div>

        {listError && (
          <div
            role="alert"
            className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400"
          >
            {listError}
          </div>
        )}

        <div className="card">
          <div className="flex items-center justify-between px-5 py-4 sm:px-6">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
              Programme
            </h3>
            {isOwner && (
              <Button size="sm" onClick={openCreate}>
                Add dose
              </Button>
            )}
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                Loading…
              </div>
            ) : entries.length === 0 ? (
              <div className="px-5 py-10 text-center text-gray-500 dark:text-gray-400">
                No programme yet. Add the first dose and every flock is measured
                against it.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-t border-gray-200 dark:border-gray-800">
                    <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      Age
                    </TableCell>
                    <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      Vaccine
                    </TableCell>
                    <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      Given by
                    </TableCell>
                    <TableCell isHeader className="px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                      Notes
                    </TableCell>
                    {isOwner && (
                      <TableCell isHeader className="px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => (
                    <TableRow
                      key={entry.id}
                      className="border-t border-gray-100 dark:border-gray-800"
                    >
                      <TableCell className="px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                        {ageLabel(entry.day_of_age)}
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          day {entry.day_of_age}
                        </span>
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-800 dark:text-white/90">
                        {entry.vaccine}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {routeLabel(entry.route)}
                      </TableCell>
                      <TableCell className="px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.notes || "—"}
                      </TableCell>
                      {isOwner && (
                        <TableCell className="px-5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => openEdit(entry)}
                            className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(entry)}
                            className="ml-4 text-sm text-gray-400 hover:text-error-500"
                          >
                            Remove
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        className="mx-4 max-w-md p-5 sm:p-8"
      >
        <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
          {editing ? "Edit programme entry" : "Add dose to programme"}
        </h4>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500"
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="vaccine-name">Vaccine *</Label>
            <Input
              id="vaccine-name"
              value={vaccine}
              onChange={(e) => setVaccine(e.target.value)}
              required
              maxLength={120}
            />
          </div>

          <div>
            <Label htmlFor="vaccine-day">Day of age *</Label>
            <Input
              id="vaccine-day"
              type="number"
              min={0}
              max={1000}
              value={dayOfAge}
              onChange={(e) => setDayOfAge(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Days since the birds arrived. 0 is day-old.
            </p>
          </div>

          <div>
            <Label htmlFor="vaccine-route">Given by *</Label>
            <select
              id="vaccine-route"
              className={selectClass}
              value={route}
              onChange={(e) => setRoute(e.target.value as VaccinationRoute)}
            >
              {VACCINATION_ROUTES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="vaccine-notes">Notes</Label>
            <Input
              id="vaccine-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={500}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={saving || !vaccine.trim() || dayOfAge === ""}>
              {saving ? "Saving…" : editing ? "Update" : "Add"}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
